import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { authApi, authError, requestOtp, retryAfter, setChallenge } from "../../../utils/auth-session";
import MobileInput from "../../Mobile/Inputs/MobileInput";
import MobileButton from "../../Mobile/Buttons/MobileButton";
import ConfirmModal from "../../ui/Modal/ConfirmModal";
import BackButton from "../../ui/BackButton/BackButton";
import Checkbox from "../../ui/Checkbox/Checkbox";
import "./CreateAccount.css";
import "../Login/Login.css"; // Reuse Login styles for absolute consistency

const CreateAccount = ({ elevvaT = (key) => key }) => {
    const navigate = useNavigate();
    const submitting = useRef(false);
    const alive = useRef(true);
    useEffect(() => { alive.current = true; return () => { alive.current = false; }; }, []);
    const [retryAt, setRetryAt] = useState(0);
    const [recoveryEmail, setRecoveryEmail] = useState("");
    const [now, setNow] = useState(Date.now());
    useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(timer); }, []);
    const [displayLoading, setDisplayLoading] = useState(false);
    const [inputs, setInputs] = useState({});
    const [modalConfig, setModalConfig] = useState({
        isOpen: false,
        title: "",
        description: "",
        iconType: null,
        primaryAction: null,
        secondaryAction: null
    });

    const closeModal = () => {
        setModalConfig(prev => ({ ...prev, isOpen: false }));
    };

    const handleChange = (event) => {
        const { name, value, type, files } = event.target;
        setInputs(prev => ({
            ...prev,
            [name]: type === "file" ? files[0] : value
        }));
    };

    const handlePhoneChange = (value) => {
        setInputs(prev => ({ ...prev, mobile_number: value }));
    };

    const handleCheckboxChange = (name, checked) => {
        setInputs(prev => ({ ...prev, [name]: checked }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (submitting.current || Date.now() < retryAt) return;

        const requiredFields = [
            "center_name", "center_nit", "email", "mobile_number",
            "password", "password2", "first_name", "last_name",
            "rut_file", "camara_comercio_file"
        ];

        const missing = requiredFields.filter(field => !inputs[field]);
        if (missing.length > 0) {
            setModalConfig({
                isOpen: true,
                title: "Campos incompletos",
                description: "Por favor completa todos los campos requeridos.",
                iconType: "info",
                primaryAction: { label: "Entendido", onClick: closeModal }
            });
            return;
        }

        if (inputs.password !== inputs.password2) {
            setModalConfig({
                isOpen: true,
                title: "Error",
                description: "Las contraseñas no coinciden.",
                iconType: "error",
                primaryAction: { label: "Entendido", onClick: closeModal }
            });
            return;
        }

        if ([inputs.rut_file, inputs.camara_comercio_file].some(file => !/\.pdf$/i.test(file.name) || (file.type && file.type !== "application/pdf"))) {
            setModalConfig({ isOpen: true, title: "Documentos en PDF", description: "Adjunta el RUT y la Cámara de Comercio en formato PDF.", iconType: "info", primaryAction: { label: "Entendido", onClick: closeModal } });
            return;
        }

        if (!inputs.terms_accepted || !inputs.privacy_policy_accepted || !inputs.advise_accepted) {
            setModalConfig({
                isOpen: true,
                title: "Acción requerida",
                description: "Debes aceptar los Términos, Políticas y la autorización de tratamiento de datos.",
                iconType: "info",
                primaryAction: { label: "Entendido", onClick: closeModal }
            });
            return;
        }

        submitting.current = true;
        setDisplayLoading(true);

        const formData = new FormData();
        formData.append("email", inputs.email.trim());
        formData.append("password", inputs.password);
        formData.append("password2", inputs.password2);
        formData.append("first_name", inputs.first_name);
        formData.append("last_name", inputs.last_name);
        formData.append("mobile_number", inputs.mobile_number);
        formData.append("company_name", inputs.center_name);
        formData.append("nit", inputs.center_nit);
        formData.append("terms_accepted", "true");
        formData.append("terms_and_conditions_accepted", "true");
        formData.append("privacy_policy_accepted", "true");
        formData.append("advise_accepted", "true");
        formData.append("rut", inputs.rut_file);
        formData.append("commerce_document", inputs.camara_comercio_file);

        try {
            const res = await authApi.post("users/auth/corp/signup/", formData);
            if (!alive.current) return;
            setChallenge(inputs.email.trim(), res.data.verification, "signup");
            navigate("/auth/verify");
        } catch (err) {
            if (!alive.current) return;
            setRetryAt(Date.now() + retryAfter(err) * 1000);
            setRecoveryEmail(inputs.email.trim());
            const msg = authError(err);

            setModalConfig({
                isOpen: true,
                title: "Error",
                description: msg,
                iconType: "error",
                primaryAction: { label: "Entendido", onClick: closeModal }
            });
        } finally {
            submitting.current = false;
            if (alive.current) setDisplayLoading(false);
        }
    };

    const resumeVerification = async () => {
        if (submitting.current || Date.now() < retryAt) return;
        submitting.current = true; setDisplayLoading(true);
        try {
            const { data } = await requestOtp(recoveryEmail);
            if (!alive.current) return;
            setChallenge(recoveryEmail, data, "signup");
            navigate("/auth/verify");
        } catch (err) {
            if (!alive.current) return;
            setRetryAt(Date.now() + retryAfter(err) * 1000);
            setModalConfig({ isOpen: true, title: "No pudimos enviar el código", description: authError(err), iconType: "info", primaryAction: { label: "Entendido", onClick: closeModal } });
        } finally { submitting.current = false; if (alive.current) setDisplayLoading(false); }
    };

    return (
        <div className="mobile-login-body-inner justify-content-start">
            {recoveryEmail && <div className="alert alert-info">Si el registro se guardó, puedes continuar verificando {recoveryEmail}.<MobileButton text={now < retryAt ? `Espera ${Math.ceil((retryAt - now) / 1000)} s` : "Solicitar código para continuar"} onClick={resumeVerification} loading={displayLoading} disabled={now < retryAt} /></div>}
            <ConfirmModal {...modalConfig} onClose={closeModal} />

            <div className="d-flex flex-column gap-3 w-100">
                <div className="w-100 mb-4 animate-fade-in-up">
                    <div className="auth-header-flex">
                        <div className="auth-nav-area">
                            <BackButton />
                        </div>
                        <h1 className="auth-main-title">Crea tu cuenta</h1>
                        <div className="auth-nav-area hidden"></div>
                    </div>
                    <div className="recover-accent-line mx-auto"></div>
                </div>

                <form className="mobile-create-account-form animate-fade-in-up delay-1" onSubmit={handleSubmit}>
                    <fieldset disabled={displayLoading} style={{ display: "contents" }}>
                    <MobileInput
                        type="text"
                        name="center_name"
                        label="Nombre del centro de entrenamiento"
                        placeholder="Ej: Elevva Gym"
                        required
                        onChange={handleChange}
                    />
                    <MobileInput
                        type="text"
                        name="center_nit"
                        label="NIT / Documento del centro"
                        placeholder="Ej: 900123456-7"
                        required
                        onChange={handleChange}
                    />
                    <div className="d-flex gap-2 mobile-input-row">
                        <MobileInput type="text" name="first_name" label="Nombre" placeholder="Tu nombre" required onChange={handleChange} />
                        <MobileInput type="text" name="last_name" label="Apellido" placeholder="Tu apellido" required onChange={handleChange} />
                    </div>
                    <MobileInput type="email" name="email" label="Correo electrónico" placeholder="ejemplo@email.com" required onChange={handleChange} />

                    <MobileInput
                        type="phone"
                        name="mobile_number"
                        label="Celular"
                        value={inputs.mobile_number}
                        required
                        onChange={handlePhoneChange}
                    />

                    <MobileInput type="password" name="password" label="Contraseña" placeholder="Mínimo 8 caracteres" required onChange={handleChange} />
                    <MobileInput type="password" name="password2" label="Confirmar contraseña" placeholder="Repite tu contraseña" required onChange={handleChange} />

                    <div className="d-flex gap-2 mobile-input-row">
                        <MobileInput
                            type="file"
                            name="rut_file"
                            label="RUT (PDF)"
                            placeholder="Seleccionar archivo"
                            value={inputs.rut_file}
                            accept=".pdf,application/pdf"
                            onChange={handleChange}
                        />
                        <MobileInput
                            type="file"
                            name="camara_comercio_file"
                            label="Cámara de Comercio (PDF)"
                            placeholder="Seleccionar archivo"
                            value={inputs.camara_comercio_file}
                            accept=".pdf,application/pdf"
                            onChange={handleChange}
                        />
                    </div>

                    <div className="mobile-terms-container">
                        <p className="mobile-terms-text">
                            Declaro que he leído, comprendido y acepto el contenido de la <a href="https://elevva.com.co/terminos-y-condiciones/" target="_blank" rel="noopener noreferrer">Política de Tratamiento de Datos Personales</a> y el <a href="https://elevva.com.co/aviso-de-privacidad/" target="_blank" rel="noopener noreferrer">Aviso de Privacidad</a>.
                        </p>

                        <div className="d-flex flex-column gap-3 mt-3">
                            <Checkbox
                                label={<>Acepto los <a href="https://elevva.com.co/terminos-y-condiciones/" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Términos y Condiciones</a></>}
                                checked={inputs.terms_accepted || false}
                                onChange={(e) => handleCheckboxChange("terms_accepted", e.target.checked)}
                                fontSize="13px"
                                checkSize="22px"
                                align="center"
                            />

                            <Checkbox
                                label={<>Acepto la <a href="https://elevva.com.co/aviso-de-privacidad/" target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>Política de Privacidad</a></>}
                                checked={inputs.privacy_policy_accepted || false}
                                onChange={(e) => handleCheckboxChange("privacy_policy_accepted", e.target.checked)}
                                fontSize="13px"
                                checkSize="22px"
                                align="center"
                            />

                            <Checkbox
                                label="He sido debidamente informado de que no estoy obligado(a) a consentir el tratamiento de mis datos sensibles y conociendo sus fines (detallados en la Política de Datos que acepto), autorizo libremente dicho tratamiento."
                                checked={inputs.advise_accepted || false}
                                onChange={(e) => handleCheckboxChange("advise_accepted", e.target.checked)}
                                fontSize="12px"
                                checkSize="22px"
                                align="flex-start"
                            />
                        </div>
                    </div>

                    <MobileButton
                        text={now < retryAt ? `Espera ${Math.ceil((retryAt - now) / 1000)} s` : "Registrarme"}
                        disabled={now < retryAt}
                        type="submit"
                        className="mt-3"
                        loading={displayLoading}
                    />
                    </fieldset>
                </form>
            </div>
        </div>
    );
};

export default React.memo(CreateAccount);
